import { Link } from "react-router-dom";
import { Copy, Eye } from "lucide-react";
import PaperSheet from "@/components/PaperSheet";
import { useTaxonomies } from "@/lib/taxonomies";
import { describeTags } from "@/lib/taxonomy";
import { formatDateBn, isRecent, teacherOf, toBn } from "@/lib/format";
import { toneHue } from "@/lib/tone";

// একটা কার্ড, দুই চেহারা: ছোট ফোনে আড়াআড়ি (বাঁয়ে মলাট, ডানে তথ্য — বাংলা শিরোনাম পুরোটা পড়া যায়),
// sm থেকে উপরে খাড়া (উপরে মলাট, নিচে তথ্য)। পুরো কার্ডটাই লিংক — বড় ট্যাপ এলাকা।
export default function PaperCard({ paper }) {
  const taxonomies = useTaxonomies();
  const tags = describeTags(taxonomies, paper);
  const pick = (key) => tags.find((t) => t.key === key);
  const cls = pick("class");
  const subject = pick("subject");
  const exam = pick("exam_type");
  const teacher = teacherOf(paper);
  const fresh = isRecent(paper.created_at);

  return (
    <Link
      to={`/papers/${paper.slug}`}
      style={{ "--tone-h": toneHue(subject?.value) }}
      className="tone group flex gap-3.5 rounded-2xl border border-border bg-card p-3 shadow-card transition duration-200 hover:border-primary/40 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-safe:hover:-translate-y-0.5 sm:flex-col sm:gap-0 sm:p-0"
    >
      {/* মলাট */}
      <div className="relative w-[92px] shrink-0 self-start rounded-xl bg-[var(--tone-bg)] p-1.5 sm:aspect-[5/4] sm:w-full sm:self-auto sm:overflow-hidden sm:rounded-b-none sm:rounded-t-2xl sm:px-[18%] sm:pb-0 sm:pt-[9%]">
        <PaperSheet
          seed={paper.slug ?? paper.id}
          institution={paper.institution}
          exam={exam?.label}
          classLabel={cls?.label}
          subjectLabel={subject?.label}
          className="transition-transform duration-300 motion-safe:group-hover:-translate-y-1"
        />
        {fresh && (
          <span className="absolute left-3 top-3 hidden items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium leading-5 text-primary-foreground shadow-sm sm:inline-flex">
            <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
            নতুন
          </span>
        )}
      </div>

      {/* তথ্য */}
      <div className="flex min-w-0 flex-1 flex-col sm:p-4">
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          {fresh && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-xs font-medium leading-5 text-primary-foreground sm:hidden">
              নতুন
            </span>
          )}
          {cls && (
            <span className="rounded-full border border-border bg-background px-2 py-0.5 text-xs leading-5 text-foreground">
              {cls.label}
            </span>
          )}
          {subject && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tone-bg)] px-2 py-0.5 text-xs font-medium leading-5 text-[var(--tone-ink)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--tone-ink-soft)]" />
              {subject.label}
            </span>
          )}
        </div>

        <h3 className="line-clamp-2 font-display text-[15px] font-semibold leading-snug text-foreground sm:text-base">
          {paper.title}
        </h3>

        {(teacher || exam) && (
          <p className="mt-1 line-clamp-1 text-[13px] text-muted-foreground">
            {[teacher, exam?.label].filter(Boolean).join(" · ")}
          </p>
        )}

        <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1" title="কতবার কপি হয়েছে">
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            {toBn(paper.copy_count ?? 0)} কপি
          </span>
          <span className="inline-flex items-center gap-1" title="কতবার দেখা হয়েছে">
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {toBn(paper.view_count ?? 0)}
          </span>
          {paper.created_at && <span className="ml-auto hidden sm:inline">{formatDateBn(paper.created_at)}</span>}
        </div>
      </div>
    </Link>
  );
}

// লোড হওয়ার সময়ের কঙ্কাল — আসল কার্ডের একই কাঠামো, তাই লোড শেষে লেআউট লাফায় না
export function PaperCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="flex animate-pulse gap-3.5 rounded-2xl border border-border bg-card p-3 sm:flex-col sm:gap-0 sm:p-0"
    >
      <div className="w-[92px] shrink-0 self-start rounded-xl bg-muted p-1.5 sm:aspect-[5/4] sm:w-full sm:self-auto sm:rounded-b-none sm:rounded-t-2xl sm:px-[18%] sm:pt-[9%]">
        <div className="aspect-[210/297] rounded-[3px] bg-card/80" />
      </div>
      <div className="flex-1 space-y-2.5 sm:p-4">
        <div className="flex gap-1.5">
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-5 w-14 rounded-full bg-muted" />
        </div>
        <div className="h-4 w-11/12 rounded bg-muted" />
        <div className="h-4 w-2/3 rounded bg-muted" />
        <div className="h-3 w-1/3 rounded bg-muted" />
      </div>
    </div>
  );
}
